import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useGetLogsByEntity, type LogEntry } from "@/api/log.api";
import { Modal } from "@/components/ui/customs/modal";
import { TimelineItem } from "@/components/ui/customs/timeline-item";
import { useLogTemplateTexts } from "@/hooks/useLogTemplateTexts";
import { cn } from "@/lib/utils";
import { DATE_TIMELINE_FORMAT, formatDateSafe } from "@/lib/logFormat";
import { LogDetail } from "@/components/backOffice/logs/logDetail";

type ContentProps = {
  entityType: string;
  entityId: string;
  enabled?: boolean;
  className?: string;
  vertical?: boolean;
};

export const LogTimelineContent = ({
  entityType,
  entityId,
  enabled = true,
  className,
  vertical = false,
}: ContentProps) => {
  const { t } = useTranslation("shared");
  const { data: logs, isLoading, isError } = useGetLogsByEntity(entityType, entityId, { enabled });

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const sortedLogs = useMemo(() => {
    if (!logs) return [];
    return [...logs].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [logs]);

  const selectedLog = useMemo(
    () => sortedLogs.find((l) => l.id === selectedId) ?? sortedLogs[0] ?? null,
    [sortedLogs, selectedId],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="size-6 animate-spin text-gray-500" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="py-8 text-center text-sm text-red-600">{t("logTimeline.loadError")}</div>
    );
  }
  if (sortedLogs.length === 0) {
    return <div className="py-8 text-center text-sm text-gray-500">{t("logTimeline.empty")}</div>;
  }

  return (
    <div
      className={cn(
        "flex overflow-hidden",
        vertical ? "flex-col gap-4" : "flex-col gap-6 md:flex-row md:gap-4",
        className,
      )}
    >
      <div
        className={cn(
          "shrink-0 overflow-y-auto rounded-lg bg-white p-2",
          vertical
            ? "max-h-[30%] border-b border-gray-100"
            : "h-[30%] border-b border-gray-100 md:h-full md:max-w-70 md:border-r md:border-b-0 lg:max-w-100",
        )}
      >
        <TimelineList
          logs={sortedLogs}
          selectedId={selectedLog?.id ?? null}
          onSelect={setSelectedId}
        />
      </div>
      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto rounded-lg bg-white p-2",
          !vertical && "md:pl-6",
        )}
      >
        {selectedLog && <LogDetail log={selectedLog} />}
      </div>
    </div>
  );
};

type DetailModalProps = {
  log: LogEntry | null;
  open: boolean;
  onClose: () => void;
};

export const LogDetailModal = ({ log, open, onClose }: DetailModalProps) => {
  const { t } = useTranslation("shared");
  if (!log) return null;
  return (
    <Modal open={open} onClose={onClose} title={t("logDetailModal.title")} size="lg" dismissible>
      <LogDetail log={log} />
    </Modal>
  );
};

type TimelineListProps = {
  logs: LogEntry[];
  selectedId: string | null;
  onSelect: (id: string) => void;
};

const TimelineList = ({ logs, selectedId, onSelect }: TimelineListProps) => {
  const { getTitle } = useLogTemplateTexts();
  return (
    <div className="flex flex-col">
      {logs.map((log, index) => {
        return (
          <TimelineItem
            key={log.id}
            dateLabel={formatDateSafe(log.createdAt, DATE_TIMELINE_FORMAT)}
            timeLabel={formatDateSafe(log.createdAt, "HH:mm")}
            title={getTitle(log.action)}
            isLast={index === logs.length - 1}
            isSelected={log.id === selectedId}
            onClick={() => onSelect(log.id)}
          />
        );
      })}
    </div>
  );
};
